// Plan Q&A System Types
// TypeScript interfaces for Plan-specific Q&A functionality

import { Timestamp } from 'firebase/firestore';

// Core conversation types
export type PlanContextType = 'goal' | 'activity' | 'tip' | 'owner_sharing' | 'parent_sharing' | 'general';
export type ConversationStatus = 'active' | 'completed';
export type ConversationTab = 'general' | 'planQa' | 'memory';
export type ConversationFlow = 'question' | 'answer' | 'clarification' | 'follow_up';

// Plan Q&A Context Interface
export interface PlanQaContext {
  planId: string;
  day: number;
  contextType: PlanContextType;
  contextElement?: string;
  planTitle: string;
  planDescription: string;
  userQuestion?: string;
}

// Extended Message Interface for Plan Q&A (extends existing Message interface)
export interface PlanQaMessage {
  // Message metadata (extends existing Message interface)
  id: string;                        // Auto-generated message ID
  content: string;                   // Message text content
  userId: string;                    // User ID or 'assistant' for AI
  role: 'user' | 'assistant';       // Message sender type
  createdAt: Timestamp;              // Message timestamp
  isEditing?: boolean;               // Edit status flag
  
  // Plan Q&A specific context (NEW - extends existing Message)
  planContext?: {
    planId: string;                  // Reference to pet care plan
    day: number;                     // Which day this message relates to
    contextType: PlanContextType;    // Type of plan element
    contextElement?: string;         // Specific element being discussed
    conversationFlow: ConversationFlow; // Type of message in conversation flow
  };
  
  // AI-specific metadata (extends existing Message)
  aiModel?: string;                  // AI model used
  tokensUsed?: number;               // Token consumption for this message
  responseTime?: number;             // Response generation time
  
  // User engagement (NEW - extends existing Message)
  isHelpful?: boolean;               // User feedback on this specific message
  userRating?: number;               // 1-5 rating for this message
  
  // References (NEW - extends existing Message)
  conversationId: string;            // Reference to parent conversation
}

// Enhanced User Plan Interface (for tracking plan Q&A usage)
export interface EnhancedUserPlan {
  // ... existing plan fields ...
  planId: string;
  day: number;
  stickers: number;
  completedActivities: string[];
  lastCompleted: Date;
  
  // Q&A conversation tracking (NEW - tracks usage in main conversations)
  dailyQaUsage?: {
    [dayNumber: string]: {
      totalQuestions: number;         // Total Q&A questions asked
      totalTokensUsed: number;        // Total AI tokens consumed
      averageResponseTime: number;    // Average AI response time
      userSatisfaction: number;       // Average user rating (1-5)
      lastQaAt: Timestamp;           // When last Q&A happened
      conversationIds: string[]       // Array of conversation IDs that contain Q&A for this day
    }
  };
  
  // Plan completion with Q&A summary (ONLY when plan is complete)
  planCompletionSummary?: {
    isGenerated: boolean;             // Whether summary has been generated
    generatedAt: Timestamp;          // When summary was generated
    totalQaQuestions: number;        // Total Q&A questions asked
    totalTokensUsed: number;         // Total AI tokens consumed
    averageUserSatisfaction: number; // Average rating across all Q&A
    keyInsights: string[];           // Main takeaways from all Q&A
    commonChallenges: string[];      // Recurring difficulties
    successfulStrategies: string[];  // What worked well
    recommendations: string[];       // AI recommendations for future plans
    dayByDaySummary: {               // Summary for each day
      [dayNumber: string]: {
        totalQuestions: number;
        keyTopics: string[];
        mainChallenges: string[];
        successfulApproaches: string[];
      }
    }
  };
}

// Legacy types (kept for backward compatibility during transition)
export interface PlanConversation {
  // Basic metadata
  conversationId: string;              // Auto-generated unique ID
  day: number;                        // Which day this conversation is about
  createdAt: Timestamp;               // When conversation started
  lastUpdated: Timestamp;             // Last message timestamp
  status: ConversationStatus;         // Conversation status
  
  // Context information
  contextType: PlanContextType;       // Type of plan element
  contextElement?: string;            // Specific element being discussed
  userQuestion: string;               // User's initial question
  
  // AI conversation metadata
  threadId?: string;                  // OpenAI thread ID for this conversation
  tokensUsed: number;                 // Total tokens consumed
  responseTime: number;               // Average response time
  
  // User engagement
  isHelpful: boolean;                 // User feedback on helpfulness
  userRating?: number;                // 1-5 rating from user
  followUpQuestions: number;          // Count of follow-up questions
  
  // Plan reference
  planId: string;                     // Reference to parent plan
  userId: string;                     // Reference to user
}

// Legacy types (kept for backward compatibility during transition)
export interface PlanConversationMessage {
  // Message metadata
  messageId: string;                  // Auto-generated message ID
  content: string;                    // Message text content
  role: 'user' | 'assistant';        // Message sender type
  createdAt: Timestamp;               // Message timestamp
  
  // Context linking
  day: number;                        // Which day this message relates to
  contextType: PlanContextType;       // Type of plan element
  contextElement?: string;            // Specific element being discussed
  
  // AI-specific metadata
  aiModel?: string;                   // AI model used
  tokensUsed?: number;                // Token consumption for this message
  responseTime?: number;              // Response generation time
  
  // User engagement
  isHelpful?: boolean;                // User feedback on this specific message
  userRating?: number;                // 1-5 rating for this message
  
  // Message context
  previousMessageId?: string;         // Reference to previous message
  nextMessageId?: string;             // Reference to next message
  conversationFlow: ConversationFlow; // Type of message in conversation flow
  
  // References
  conversationId: string;             // Reference to parent conversation
  planId: string;                     // Reference to plan
  userId: string;                     // Reference to user (either user ID or 'assistant')
}

// AI Response Data Interface
export interface AiResponseData {
  content: string;
  tokensUsed: number;
  responseTime: number;
  model: string;
}

// Language Context Interface
export interface LanguageContext {
  detectedLanguage: 'zh-CN' | 'es' | 'fr' | 'en';
  confidence: number;
}

// Data creation types for the new linked approach
export interface CreatePlanQaMessageData {
  content: string;
  role: 'user' | 'assistant';
  planContext: {
    planId: string;
    day: number;
    contextType: PlanContextType;
    contextElement?: string;
    conversationFlow: ConversationFlow;
  };
  tokensUsed?: number;
  responseTime?: number;
  isHelpful?: boolean;
  userRating?: number;
}

// Legacy types for backward compatibility
export interface CreatePlanConversationData {
  day: number;
  status: ConversationStatus;
  contextType: PlanContextType;
  contextElement?: string;
  userQuestion: string;
  planId: string;
  userId: string;
}

export interface CreatePlanMessageData {
  content: string;
  role: 'user' | 'assistant';
  day: number;
  contextType: PlanContextType;
  contextElement?: string;
  conversationFlow: ConversationFlow;
  aiModel?: string;
  tokensUsed?: number;
  responseTime?: number;
}

// Error handling
export class PlanQaError extends Error {
  constructor(
    message: string,
    public code: PlanQaErrorCodes,
    public originalError?: Error | unknown
  ) {
    super(message);
    this.name = 'PlanQaError';
  }
}

export enum PlanQaErrorCodes {
  AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
  INVALID_PLAN_CONTEXT = 'INVALID_PLAN_CONTEXT',
  DATABASE_ERROR = 'DATABASE_ERROR',
  AI_SERVICE_ERROR = 'AI_SERVICE_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AI_GENERATION_FAILED = 'AI_GENERATION_FAILED',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED'
}

// Tab Configuration
export interface ConversationTabConfig {
  id: ConversationTab;
  label: string;
  icon: string;
}

export const CONVERSATION_TABS: readonly ConversationTabConfig[] = [
  { id: 'general', label: 'General Chat', icon: 'ChatIcon' },
  { id: 'planQa', label: 'Plan Q&A', icon: 'QuestionAnswerIcon' }
] as const;

// UI Component Props Interfaces
export interface PlanContextBannerProps {
  planId: string;
  day: number;
  contextType: PlanContextType;
  planTitle: string;
  planDescription: string;
  contextElement?: string;
}
